'use client'
import Link from 'next/link'
import { Suspense } from 'react'
import { usePathname } from 'next/navigation'
import clsx from 'clsx'

import { MobileMenu } from './MobileMenu'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/providers/Auth'
import { UserIcon, ChevronDown } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ResolvedLink } from '@/utilities/resolveNavigationLink'
import { userHasScannerAccess } from '@/utilities/scannerAccess'

export type HeaderClientProps = {
  headerLinks: ResolvedLink[]
}

import { LogoIcon } from '@/components/icons/logo'

function DesktopAccountMenu() {
  const { user } = useAuth()

  if (user) {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="hidden lg:flex items-center gap-2 rounded-full border border-border/50 bg-card px-4 py-2 text-sm font-medium text-foreground shadow-sm transition-all hover:border-primary/30 hover:bg-card hover:shadow-md"
          >
            <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10">
              <UserIcon className="h-4 w-4 text-primary" />
            </div>
            <span className="max-w-[120px] truncate">{user.name || user.email?.split('@')[0]}</span>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52 rounded-xl p-2">
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg px-3 py-2.5">
            <Link href="/account">Mijn account</Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild className="cursor-pointer rounded-lg px-3 py-2.5">
            <Link href="/orders">Mijn cursussen</Link>
          </DropdownMenuItem>
          <DropdownMenuSeparator className="my-2" />
          <DropdownMenuItem
            asChild
            className="cursor-pointer rounded-lg px-3 py-2.5 text-destructive focus:text-destructive"
          >
            <Link href="/logout">Uitloggen</Link>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    )
  }

  return (
    <div className="hidden lg:flex items-center gap-3">
      <Button asChild variant="ghost" size="sm" className="rounded-full px-5 text-sm font-medium">
        <Link href="/inloggen">Inloggen</Link>
      </Button>
      <Button
        asChild
        size="sm"
        className="rounded-full px-5 text-sm font-medium shadow-md shadow-primary/20 transition-shadow hover:shadow-lg hover:shadow-primary/25"
      >
        <Link href="/registreren">Registreren</Link>
      </Button>
    </div>
  )
}

export function HeaderClient({ headerLinks }: HeaderClientProps) {
  const pathname = usePathname()
  const { user } = useAuth()
  const canUseScanner = userHasScannerAccess(user)

  return (
    <header className="border-b border-border/30 bg-background">
      <nav className="container relative flex h-18 items-center justify-between gap-4 py-3">
        <div className="flex w-full items-center justify-between gap-6">
          {/* Logo */}
          <Link
            className="group flex shrink-0 items-center gap-3 rounded-2xl px-3 py-2 text-foreground transition-all hover:bg-muted/40"
            href="/"
          >
            <div className="relative">
              <LogoIcon className="h-10 w-10 transition-transform duration-300 group-hover:scale-110" />
              <div className="absolute inset-0 rounded-full bg-primary/10 opacity-0 blur-xl transition-opacity duration-300 group-hover:opacity-100" />
            </div>
            <span className="text-base font-bold tracking-tight text-foreground">
              Glutenvrijadvies.nl
            </span>
          </Link>

          {/* Desktop Navigation Links - Centered */}
          <div className="hidden lg:flex items-center justify-center gap-1 absolute left-1/2 -translate-x-1/2">
            <Link
              href="/cursussen"
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                pathname === '/cursussen' || pathname?.startsWith('/cursussen/')
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground/80 hover:text-foreground hover:bg-muted/50',
              )}
            >
              Cursussen
            </Link>
            <Link
              href="/blog"
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                pathname === '/blog' || pathname?.startsWith('/blog/')
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground/80 hover:text-foreground hover:bg-muted/50',
              )}
            >
              Blog
            </Link>
            <Link
              href="/vezelkaarten"
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                pathname === '/vezelkaarten'
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground/80 hover:text-foreground hover:bg-muted/50',
              )}
            >
              Vezelkaarten
            </Link>
            {canUseScanner && (
              <Link
                href="/glutenvrij-scanner"
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  pathname === '/glutenvrij-scanner'
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/80 hover:text-foreground hover:bg-muted/50',
                )}
              >
                Scanner
              </Link>
            )}
            <Link
              href="/contact"
              className={clsx(
                'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                pathname === '/contact'
                  ? 'bg-primary/10 text-primary'
                  : 'text-foreground/80 hover:text-foreground hover:bg-muted/50',
              )}
            >
              Contact
            </Link>
            {headerLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                target={link.newTab ? '_blank' : undefined}
                rel={link.newTab ? 'noopener noreferrer' : undefined}
                className={clsx(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-colors',
                  pathname === link.href
                    ? 'bg-primary/10 text-primary'
                    : 'text-foreground/80 hover:text-foreground hover:bg-muted/50',
                )}
              >
                {link.label}
              </Link>
            ))}
          </div>

          <div className="flex items-center gap-3">
            {/* Desktop Account Menu */}
            <DesktopAccountMenu />
            {/* Mobile hamburger */}
            <div className="lg:hidden">
              <Suspense fallback={null}>
                <MobileMenu headerLinks={headerLinks} />
              </Suspense>
            </div>
          </div>
        </div>
      </nav>
    </header>
  )
}
